import { CheckCircleIcon, ChevronUpIcon } from "@talladega/icons";
import { ExtendedWarrantyRatesResponse } from "@modules/purchase/warranty/types/ExtendedWarrantyRates";
import { IAExtendedWarrantyPrograms } from "@modules/purchase/warranty/types/IAProgram";
import warrantyComponentsByProgram, {
  WarrantyComponent as WarrantyComponentType,
} from "@modules/purchase/warranty/types/WarrantyComponent";
import { useTranslation, Trans } from "next-i18next";
import { Disclosure, Transition } from "@headlessui/react";
import classNames from "classnames";
import {
  EW_ADDITIONAL_VEHICLE_COMPONENTS,
  EW_HYBRID_ELECTRIC,
} from "@common/constants";
import { getPrefixUrl } from "@modules/api/api";
import { useRouter } from "next/router";

type Props = {
  extendedWarrantyDefaultRates: ExtendedWarrantyRatesResponse;
};

const getsampleHybridEvComponentsContractLink = (locale: string) => {
  return `${getPrefixUrl()}/api/purchases/contracts/sample/ext-warranty-hybrid-ev?lang=${locale}`;
};

const WarrantyComponent = ({ extendedWarrantyDefaultRates }: Props) => {
  const { t } = useTranslation(["common", "purchase"]);
  const locale = useRouter()?.locale || "en";

  const iaProgram = IAExtendedWarrantyPrograms.find(
    (p) => p.id === extendedWarrantyDefaultRates?.productDetails?.programId
  );

  const distinctUsedCoverageTypeIds = extendedWarrantyDefaultRates?.success
    ? extendedWarrantyDefaultRates?.coverages.reduce<string[]>(
        (acc, current) => {
          if (!acc.some((coverage) => coverage === current.type)) {
            acc.push(current.type);
          }
          return acc;
        },
        []
      )
    : [];

  const usedCoverages =
    iaProgram?.coverageTypes.filter((ct) =>
      distinctUsedCoverageTypeIds.includes(ct.abbr)
    ) ?? [];

  const warrantyComponents =
    iaProgram != null && iaProgram.id in warrantyComponentsByProgram
      ? warrantyComponentsByProgram[iaProgram.id]
      : [];

  // filter out components that match 0 returned coverage types
  const filteredWarrantyComponents = warrantyComponents.filter((wc) =>
    wc.coverages.some((c) => distinctUsedCoverageTypeIds.includes(c))
  );

  const onlyAMA = usedCoverages.length === 1 && usedCoverages[0].abbr === "MA";

  const renderComponentContent = (component: WarrantyComponentType) => {
    const onlySuperiorWrap =
      usedCoverages.length === 1 && usedCoverages[0].abbr === "SW";

    switch (component.id) {
      case EW_ADDITIONAL_VEHICLE_COMPONENTS:
        return (
          <div
            id="additional-vehicle-components"
            className="flex w-full flex-col items-start gap-6 px-2 font-medium text-gray-500"
          >
            {t("purchase:some_examples_additional_components")}
            <span className="font-normal">{t(component.details.i18nkey)}</span>
            {t("purchase:always_refer_to_contract_details")}
          </div>
        );
      case EW_HYBRID_ELECTRIC:
        return (
          <div id="hybrid-electric-components" className="flex flex-col gap-10">
            <div>
              {t(component.details.i18nkey)}
              <a
                href="#"
                rel="noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(
                    getsampleHybridEvComponentsContractLink(locale),
                    "_blank"
                  );
                }}
              >
                <span className="ml-2 text-primary-500 underline">
                  {t("purchase:view_sample_hybrid_ev_contract")}
                </span>
              </a>
            </div>
            <div>
              <p className="font-bold">
                {t("purchase:warranty_coverage_on_propulsion")}
              </p>
              <p>{t("purchase:warranty_coverage_on_propulsion_body")}</p>
            </div>
            <div>
              <p>
                <Trans
                  t={t}
                  i18nKey="purchase:repairing_replacing_if_necessary"
                  components={{ b: <span className="font-bold" /> }}
                />
              </p>
              <p>{t("purchase:repairing_replacing_if_necessary_body")}</p>
            </div>
            <div>
              <p>
                <Trans
                  t={t}
                  i18nKey="purchase:coverage_of_residential_charging_stations"
                  components={{ b: <span className="font-bold" /> }}
                />
              </p>
              <p>
                {t("purchase:coverage_of_residential_charging_stations_body")}
              </p>
            </div>
          </div>
        );

      default:
        return (
          <div>
            <p>{t(component.details.i18nkey)}</p>

            {/* Specific coverage perks section. (Only Superior Wrap at the moment) */}
            {component?.details?.coverageSpecificComponents &&
              !onlySuperiorWrap && (
                <div
                  id="specific-coverage"
                  key={`${component.id}-specific-coverage`}
                  className="mt-6"
                >
                  <div key={`${component.id}-details`}>
                    <p className="font-bold text-primary-500">
                      <Trans
                        t={t}
                        i18nKey="purchase:coverage_only_perks"
                        values={{
                          coverageName: t(
                            component.details.coverageSpecificComponents
                              .coverageType.i18nKey
                          ),
                        }}
                      />
                    </p>
                    <ul className="list-inside list-disc">
                      {t(component.details.coverageSpecificComponents.i18nkey)
                        .split(",")
                        .map((item: string) => (
                          <li key={item}>{item}</li>
                        ))}
                    </ul>
                  </div>
                </div>
              )}
          </div>
        );
    }
  };

  return (
    <>
      <div className="flex items-center gap-1 py-2 sm:gap-4">
        <h5 className="flex-1 overflow-x-clip overflow-ellipsis px-2 text-sm">
          {onlyAMA
            ? t("purchase:ew_coverage_component_heading_ama")
            : t("purchase:components")}
        </h5>

        {usedCoverages.map((coverage) => (
          <h5
            key={`coverage-type-${coverage.abbr}`}
            className="w-[18.7%] overflow-x-clip overflow-ellipsis text-center text-sm sm:w-[18%]"
          >
            {usedCoverages.length === 1
              ? t("purchase:ew_program_covered")
              : t(coverage.i18nKey)}
          </h5>
        ))}
      </div>
      {filteredWarrantyComponents.map((component) => (
        <Disclosure
          key={component.id}
          defaultOpen={component.id === EW_ADDITIONAL_VEHICLE_COMPONENTS}
        >
          {({ open }) => (
            <div
              key={`${component.id}-content`}
              className={classNames(
                "flex flex-col items-start border-b-2 border-gray-50 sm:text-base",
                {
                  "border-transparent": open,
                }
              )}
            >
              <Disclosure.Button
                className={classNames(
                  "flex w-full justify-between bg-white py-3 text-left focus:outline-none",
                  {
                    "rounded-b-none": open,
                    "bg-gray-50": open,
                    "hover:bg-gray-25": !open,
                  }
                )}
              >
                <div className="flex w-full items-center gap-1 sm:gap-4">
                  <div className="flex flex-1 items-center overflow-x-clip pl-1 pr-2 sm:px-2">
                    {component.icon && (
                      <div className="mr-2 flex-none">{component.icon}</div>
                    )}

                    <div className="w-fit text-sm font-medium sm:text-base">
                      {t(component.name)}
                    </div>

                    <div className="flex-none pl-1">
                      <ChevronUpIcon
                        className={`${
                          open
                            ? "text-primary-500"
                            : "rotate-180 transform text-primary"
                        } h-6 w-6`}
                      />
                    </div>
                  </div>

                  {usedCoverages.map((coverage) => (
                    <div
                      key={`warranty-components-coverage-${component.id}-${coverage.abbr}`}
                      className="w-[18.7%] text-center sm:w-[18%]"
                    >
                      {component.coverages.includes(coverage.abbr) ? (
                        <CheckCircleIcon className="inline h-5 w-5 text-secondary-700" />
                      ) : (
                        <span className="text-gray-200">—</span>
                      )}
                    </div>
                  ))}
                </div>
              </Disclosure.Button>
              <Transition
                unmount={false}
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
                className="w-full bg-gray-25"
              >
                <Disclosure.Panel unmount={false} className="p-4">
                  <div className="text-start text-sm sm:text-base">
                    {renderComponentContent(component)}
                  </div>
                </Disclosure.Panel>
              </Transition>
            </div>
          )}
        </Disclosure>
      ))}
    </>
  );
};

export default WarrantyComponent;
